'use client';

import { useState } from 'react';
import { FileText, Grid3x3, List, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { Skeleton } from '@kit/ui/skeleton';
import { Trans } from '@kit/ui/trans';

import { useDocumentViewer } from '../hooks/use-document-viewer';
import type { Document, DocumentViewMode } from '../types';
import { DocumentCard } from './document-card';
import { DocumentRow } from './document-row';
import { DocumentViewerDialog } from './document-viewer-dialog';

interface DocumentListProps<T extends Document = Document> {
  documents: T[];
  onView?: (document: T) => void;
  onDelete?: (documentId: string) => Promise<void>; // i18n-ignore TS type signature, not user-facing copy
  onUpload?: () => void;
  showActions?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
  defaultViewMode?: DocumentViewMode;
  /** Whether to show the grid/list view toggle. Defaults to true. */
  showViewToggle?: boolean;
  /** Whether to show the header with document count and controls. Defaults to true. */
  showHeader?: boolean;
  /** Show download button inline at the bottom of cards instead of in dropdown. Defaults to false. */
  inlineDownload?: boolean;
  className?: string;
  renderBadge?: (document: T) => React.ReactNode;
  renderMeta?: (document: T) => React.ReactNode;
}

interface EmptyStateProps {
  message?: string;
  onUpload?: () => void;
}

function EmptyState({ message, onUpload }: EmptyStateProps) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-12">
        <FileText className="text-muted-foreground mb-4 h-12 w-12" />
        <h3 className="mb-2 text-lg font-semibold">
          <Trans i18nKey="documents:list.emptyTitle" />
        </h3>
        <p className="text-muted-foreground mb-4 text-center text-sm">
          {message ?? <Trans i18nKey="documents:list.emptyMessage" />}
        </p>
        {onUpload && (
          <Button onClick={onUpload}>
            <Upload className="mr-2 h-4 w-4" />
            <Trans i18nKey="documents:list.upload" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function LoadingState({ viewMode }: { viewMode: DocumentViewMode }) {
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <Skeleton className="aspect-square w-full" />
            <CardContent className="p-4">
              <Skeleton className="mb-2 h-4 w-full" />
              <Skeleton className="h-3 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  );
}

export function DocumentList<T extends Document = Document>({
  documents,
  onView,
  onDelete,
  onUpload,
  showActions = true,
  isLoading = false,
  emptyMessage,
  defaultViewMode = 'grid',
  showViewToggle = true,
  showHeader = true,
  inlineDownload = false,
  className = '',
  renderBadge,
  renderMeta,
}: DocumentListProps<T>) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<DocumentViewMode>(defaultViewMode);
  const viewer = useDocumentViewer(documents as Document[]);

  const handleView = (document: T) => {
    if (onView) {
      onView(document);
    } else {
      viewer.open(document as Document);
    }
  };

  const handleDelete = (documentId: string) => async () => {
    if (onDelete) await onDelete(documentId);
  };

  if (isLoading) return <LoadingState viewMode={viewMode} />;

  if (documents.length === 0) {
    return <EmptyState message={emptyMessage} onUpload={onUpload} />;
  }

  return (
    <div className={className}>
      {showHeader && (
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            <Trans
              i18nKey="documents:list.titleWithCount"
              values={{ count: documents.length }}
            />
          </h3>
          <div className="flex items-center gap-2">
            {onUpload && (
              <Button variant="outline" size="sm" onClick={onUpload}>
                <Upload className="mr-1 h-4 w-4" />
                <Trans i18nKey="documents:list.uploadShort" />
              </Button>
            )}
            {showViewToggle && (
              <div className="flex items-center gap-1 rounded-md border p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="h-8 w-8 p-0"
                  aria-label={t('documents:list.gridView')}
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-8 w-8 p-0"
                  aria-label={t('documents:list.listView')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {documents.map((document) => (
            <DocumentCard
              key={document.id}
              document={document}
              onView={() => handleView(document)}
              onDelete={onDelete ? handleDelete(document.id) : undefined}
              showActions={showActions}
              inlineDownload={inlineDownload}
              renderBadge={renderBadge}
              renderMeta={renderMeta}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((document) => (
            <DocumentRow
              key={document.id}
              document={document}
              onView={() => handleView(document)}
              onDelete={onDelete ? handleDelete(document.id) : undefined}
              showActions={showActions}
              renderBadge={renderBadge}
            />
          ))}
        </div>
      )}

      {!onView && (
        <DocumentViewerDialog
          document={viewer.currentDocument}
          allDocuments={documents as Document[]}
          open={viewer.isOpen}
          onOpenChange={(open) => {
            if (!open) viewer.close();
          }}
          currentIndex={viewer.currentIndex}
          onNavigate={viewer.navigate}
          hasPrevious={viewer.hasPrevious}
          hasNext={viewer.hasNext}
          zoom={viewer.zoom}
          onZoomChange={viewer.setZoom}
          onZoomIn={viewer.zoomIn}
          onZoomOut={viewer.zoomOut}
          onResetZoom={viewer.resetZoom}
          isFullscreen={viewer.isFullscreen}
          onToggleFullscreen={viewer.toggleFullscreen}
        />
      )}
    </div>
  );
}
